import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon, MagnifyingGlassIcon, ExclamationTriangleIcon, CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import patientService from '../../services/patientService';
import { errText } from '../../services/scheduleService';
import { ageFrom, fmtDob } from '../../utils/saId';
import PatientEditor from './PatientEditor';
import InvitePatientModal from './InvitePatientModal';
import OnboardingPanel from './OnboardingPanel';

const SEX = { M: 'Male', F: 'Female' };
const REL = { main_member: 'Main member', spouse: 'Spouse', child: 'Child', other: 'Dependant' };

/**
 * Patients — find, add and edit the practice's patients and their medical aid
 * cover: the details a claim to a medical scheme needs.
 */
const Patients = () => {
  const [q, setQ] = useState('');
  const [inactive, setInactive] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schemes, setSchemes] = useState([]);
  const [editing, setEditing] = useState(null); // { id: number | null }
  const [notice, setNotice] = useState(null); // { text, warnings }
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [onboardingKey, setOnboardingKey] = useState(0);

  const load = useCallback(async (query, showInactive, reviewOnly) => {
    setLoading(true);
    setError('');
    try {
      setRows(await patientService.search(query, { inactive: showInactive, review: reviewOnly ? 'pending' : undefined }));
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim(), inactive, pendingOnly), 250);
    return () => clearTimeout(t);
  }, [q, inactive, pendingOnly, load]);

  useEffect(() => {
    patientService.schemes().then(setSchemes).catch((e) => setError(errText(e)));
  }, []);

  const onSaved = (patient, warnings = []) => {
    setEditing(null);
    setNotice({ text: `${patient.first_names} ${patient.surname} saved.`, warnings });
    load(q.trim(), inactive, pendingOnly);
    setOnboardingKey((k) => k + 1);
  };

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">Patients</h1>
          <p className="mt-1 text-slate-500">Demographics and medical aid details — what a claim to a medical scheme needs.</p>
        </div>
        <div className="flex shrink-0 gap-2">
        <button
          onClick={() => setInviteOpen(true)}
          title="Email the patient a link to fill in their own details"
          className="inline-flex items-center gap-2 rounded-lg border border-brand-navy bg-white px-4 py-2.5 text-sm font-semibold text-brand-navy hover:bg-slate-50"
        >
          <EnvelopeIcon className="h-5 w-5" /> Invite patient
        </button>
        <button
          onClick={() => setEditing({ id: null })}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95"
        >
          <PlusIcon className="h-5 w-5" /> New patient
        </button>
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {notice && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><CheckCircleIcon className="h-5 w-5" />{notice.text}</span>
            <button onClick={() => setNotice(null)} className="text-xs font-semibold text-green-700 hover:underline">Dismiss</button>
          </div>
          {notice.warnings.map((w) => (
            <div key={w} className="mt-1 flex items-center gap-2 text-amber-700"><ExclamationTriangleIcon className="h-4 w-4" />{w}</div>
          ))}
        </div>
      )}

      <OnboardingPanel refreshKey={onboardingKey} onChanged={() => load(q.trim(), inactive, pendingOnly)} onOpenPatient={(id) => setEditing({ id })} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[18rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, ID number, membership number or cell"
            className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-500">
          <input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} className="accent-brand-navy" />
          Include inactive
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-500">
          <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} className="accent-brand-navy" />
          Awaiting review only
        </label>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold">Patient</th>
                <th className="px-4 py-3 font-semibold">Born</th>
                <th className="px-4 py-3 font-semibold">Medical aid</th>
                <th className="px-4 py-3 font-semibold">Cell</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && rows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">{q ? 'No patients match.' : 'No patients yet — add the first one.'}</td></tr>
              ) : rows.map((p) => (
                <tr key={p.patient_id} onClick={() => setEditing({ id: p.patient_id })} className={`cursor-pointer hover:bg-slate-50 ${p.is_active ? '' : 'opacity-50'}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">
                      {p.surname}, {p.first_names}
                      {p.review_status === 'pending_review' && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Awaiting review</span>}
                      {p.review_status === 'rejected' && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Rejected</span>}
                    </div>
                    <div className="text-xs text-slate-400">
                      {[p.title, p.initials].filter(Boolean).join(' ')}
                      {p.id_number ? ` · ${p.id_type === 'passport' ? 'Passport ' : 'ID '}${p.id_number}` : ''}
                      {!p.is_active && ' · inactive'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {fmtDob(p.date_of_birth)}
                    <div className="text-xs text-slate-400">{ageFrom(p.date_of_birth)} y · {SEX[p.sex]}</div>
                  </td>
                  <td className="px-4 py-3">
                    {p.payer === 'private' ? (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">Private</span>
                    ) : p.membership_number ? (
                      <>
                        <div className="text-slate-700">{p.scheme_name}{p.plan_option ? ` · ${p.plan_option}` : ''}</div>
                        <div className="text-xs text-slate-400">{p.membership_number} / {p.dependant_code} · {REL[p.relationship]}</div>
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                        <ExclamationTriangleIcon className="h-4 w-4" /> No medical aid details
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.cell_phone || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {inviteOpen && <InvitePatientModal onClose={() => setInviteOpen(false)} onSent={() => setOnboardingKey((k) => k + 1)} />}

      {editing && (
        <PatientEditor
          patientId={editing.id}
          schemes={schemes}
          onSchemesChange={setSchemes}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onOpenPatient={(id) => setEditing({ id })}
        />
      )}
    </div>
  );
};

export default Patients;
